/**
 * Profiles repository — the single chokepoint for `profiles` table access in
 * the a14 slice (Great Migration N-3, Workstream B/C). Hooks/components call
 * the repository, never `supabase.from("profiles")` directly.
 *
 * Profiles are USER-keyed (`id` = auth user id), not company-keyed — every
 * query here scopes by `.eq("id", userId)` (or the phone-variant lookup behind
 * the super-admin gate), never by company_id.
 *
 * TRANSPORT MAP (gated on `API_URL`, Supabase fallback intact):
 *   - getById / getHasPassword          → `GET /profiles/me` (SELF only)
 *   - updateSelf                        → `PATCH /profiles/me` (write rule §2.4:
 *     definitive 4xx RETHROWS — a 403 falling back to Supabase would BYPASS the
 *     server-side Nafath identity-field lock through the legacy RLS path)
 *   - isEmailTaken                      → `GET /profiles/email-taken`
 *   - superAdminLookupById / ByPhone    → `GET /profiles/super-admin/lookup`
 *   - getByIdDirect                     → Supabase ONLY (impersonated-target view
 *     + the session-boot read — D-track owns both; the /me endpoint is
 *     self-keyed and cannot serve another user's row)
 *   - getImpersonationTarget            → Supabase ONLY (D-track / S-5 act-as
 *     replaces the whole mechanism)
 *
 * Error-shape parity is pinned per method to the call sites this consolidation
 * replaced — some swallow Supabase errors by design (see each method's note).
 */
import { supabase } from "@/integrations/supabase/client";
import type { Database } from "@/integrations/supabase/types";
import type { PostgrestError } from "@supabase/supabase-js";
import { API_ROUTES } from "@/api/routes";
import { API_URL } from "@/services/api";
import { makeNestRequest } from "@/services/nestRequest";
import {
  reportNestFallback,
  reportNestFallbackOrRethrowDefinitive,
} from "@/lib/nestFallback";
import {
  profileMeResponseSchema,
  emailTakenResponseSchema,
  superAdminProfileLookupResponseSchema,
} from "@dallali/shared";

const nestRequest = makeNestRequest("profiles");

export type ProfileRow = Database["public"]["Tables"]["profiles"]["Row"];
type ProfileUpdate = Database["public"]["Tables"]["profiles"]["Update"];

/**
 * The self-editable column whitelist — mirrors the `PATCH /profiles/me` DTO.
 * Typing `updateSelf` against this (not the full ProfileUpdate) makes the two
 * transports structurally identical: a column outside the server whitelist
 * cannot be passed at all, so it can neither be silently stripped by Nest nor
 * silently written by the Supabase fallback.
 */
export type ProfileSelfUpdate = Pick<
  ProfileUpdate,
  | "full_name"
  | "full_name_en"
  | "phone"
  | "address"
  | "national_id"
  | "avatar_url"
  | "preferred_language"
  | "fal_license_number"
  | "fal_license_image_url"
>;

/** Cross-tenant lookup projection (GrantSuperAdminDialog contract). */
export type ProfileLookupRow = Pick<
  ProfileRow,
  "id" | "full_name" | "full_name_en" | "phone" | "nafath_verified"
>;

/**
 * Narrow a parsed lookup-response profile to the Supabase-typed row. Under
 * this app's `strict: false` tsconfig, zod's `z.infer` degrades object keys
 * to optional, so the parse result cannot be assigned to ProfileLookupRow
 * directly — rebuild it, treating an id-less payload as "not found" (the
 * documented null contract for both lookups).
 */
function toProfileLookupRow(
  p:
    | {
        id?: string;
        full_name?: string | null;
        full_name_en?: string | null;
        phone?: string | null;
        nafath_verified?: boolean | null;
      }
    | null
    | undefined,
): ProfileLookupRow | null {
  if (!p?.id) return null;
  return {
    id: p.id,
    full_name: p.full_name ?? null,
    full_name_en: p.full_name_en ?? null,
    phone: p.phone ?? null,
    nafath_verified: p.nafath_verified ?? null,
  };
}

/** Impersonation-banner enrichment projection (ImpersonationContext contract). */
export type ImpersonationTargetProfile = Pick<
  ProfileRow,
  "full_name" | "full_name_en" | "avatar_url" | "phone" | "email"
>;

export const profilesRepository = {
  /**
   * The caller's OWN profile row (full Row shape), or null when absent.
   * SELF ONLY — the Nest path serves `GET /profiles/me`, keyed on the JWT.
   * For another user's row (impersonated view, boot read) use
   * `getByIdDirect`. Throws on a Supabase error (callers that historically
   * swallowed errors catch locally).
   */
  async getById(userId: string): Promise<ProfileRow | null> {
    if (API_URL) {
      try {
        const res = await nestRequest<unknown>(API_ROUTES.profiles.me());
        return profileMeResponseSchema.parse(res) as ProfileRow;
      } catch (err) {
        reportNestFallback("profiles.getById", err);
      }
    }
    return fetchProfileRow(userId);
  },

  /**
   * A profile row by id over the Supabase transport ONLY — the
   * impersonated-target view (ProfileTab) and the session-boot read
   * (useAuth.fetchUserData, D3-owned). Deliberately NOT flipped: `/profiles/me`
   * is self-keyed and the boot/impersonation paths stay Supabase-internal
   * until their D-track slices move them.
   */
  async getByIdDirect(userId: string): Promise<ProfileRow | null> {
    return fetchProfileRow(userId);
  },

  /**
   * Whether the caller has an email password set (profiles.has_password).
   * SELF ONLY — rides `GET /profiles/me` on the Nest path (no separate
   * endpoint). Supabase errors resolve to false (the EmailManagementCard
   * check ignored them by design — an availability blip must not flip the UI
   * into the "set password" flow with a thrown error).
   */
  async getHasPassword(userId: string): Promise<boolean> {
    if (API_URL) {
      try {
        const res = await nestRequest<unknown>(API_ROUTES.profiles.me());
        return profileMeResponseSchema.parse(res).has_password;
      } catch (err) {
        reportNestFallback("profiles.getHasPassword", err);
      }
    }
    const { data } = await supabase
      .from("profiles")
      .select("has_password")
      .eq("id", userId)
      .maybeSingle();
    return !!data?.has_password;
  },

  /**
   * Whether ANY profile holds this email (lowercased). Boolean only — never
   * the row. Supabase errors resolve to false (parity with the ProfileTab
   * checks, which destructured `data` and ignored `error` — a probe failure
   * falls through to the server-side uniqueness constraint).
   */
  async isEmailTaken(email: string): Promise<boolean> {
    const normalized = email.trim().toLowerCase();
    if (API_URL) {
      try {
        const res = await nestRequest<unknown>(
          API_ROUTES.profiles.emailTaken(normalized),
        );
        return emailTakenResponseSchema.parse(res).taken;
      } catch (err) {
        reportNestFallback("profiles.isEmailTaken", err);
      }
    }
    const { data } = await supabase
      .from("profiles")
      .select("id")
      .eq("email", normalized)
      .maybeSingle();
    return !!data;
  },

  /**
   * Update the caller's OWN profile (whitelisted columns only — see
   * ProfileSelfUpdate). Throws on failure; callers that historically
   * fire-and-forgot (avatar auto-save, Nafath autofill) catch locally.
   *
   * WRITE RULE (§2.4): the Nest path rethrows a definitive 4xx — critically,
   * the companies-003 Nafath-lock 403 must NOT fall back to the Supabase
   * update, which would bypass the server-side identity-field lock through
   * the legacy RLS path. Transport failures fall back (idempotent write).
   */
  async updateSelf(userId: string, updates: ProfileSelfUpdate): Promise<void> {
    if (API_URL) {
      try {
        const res = await nestRequest<unknown>(API_ROUTES.profiles.me(), {
          method: "PATCH",
          body: JSON.stringify(updates),
        });
        profileMeResponseSchema.parse(res);
        return;
      } catch (err) {
        reportNestFallbackOrRethrowDefinitive("profiles.updateSelf", err);
      }
    }
    const { error } = await supabase
      .from("profiles")
      .update(updates)
      .eq("id", userId);
    if (error) throw error;
  },

  /**
   * Whether a user's profile is Nafath-verified (EJAR import verified-tenant
   * guard: a verified profile's tenant record is authoritative and must not
   * be overwritten from a PDF). Supabase ONLY. NEVER throws — the
   * pre-consolidation site destructured `{ data }` only (an unreadable
   * profile counts as unverified, so the admin's reviewed data wins).
   */
  async isNafathVerified(userId: string): Promise<boolean> {
    const { data } = await supabase
      .from("profiles")
      .select("nafath_verified")
      .eq("id", userId)
      .maybeSingle();
    return !!data?.nafath_verified;
  },

  /**
   * Stamp the caller's OWN profile as Nafath-verified (post-verification
   * persist from the ads wizard — deliberately outside the ProfileSelfUpdate
   * whitelist, which mirrors the /profiles/me DTO; this column is set by the
   * verification flow, not user-editable). Supabase ONLY. Returns the error
   * instead of throwing — the wizard fire-and-forgets and Sentry-captures.
   */
  async markSelfNafathVerified(
    userId: string,
  ): Promise<{ error: PostgrestError | null }> {
    const { error } = await supabase
      .from("profiles")
      .update({
        nafath_verified: true,
        nafath_verified_at: new Date().toISOString(),
      })
      .eq("id", userId);
    return { error };
  },

  /**
   * Banner enrichment for an impersonation target (name/avatar/contact).
   * Supabase ONLY — the D-track S-5 act-as flow replaces this mechanism.
   * Errors resolve to null (the context destructured `data` and ignored
   * `error`; enrichment is best-effort on top of the caller-supplied target).
   */
  async getImpersonationTarget(
    userId: string,
  ): Promise<ImpersonationTargetProfile | null> {
    const { data } = await supabase
      .from("profiles")
      .select("full_name, full_name_en, avatar_url, phone, email")
      .eq("id", userId)
      .maybeSingle();
    return data ?? null;
  },

  /**
   * Cross-tenant profile lookup by user id (Grant-Super-Admin dialog).
   * Errors resolve to null (parity with the dialog's `lookupByUserId`, which
   * returned null on any profile error). Nest path: the
   * JwtSuperAdminGuard-gated lookup endpoint.
   */
  async superAdminLookupById(userId: string): Promise<ProfileLookupRow | null> {
    if (API_URL) {
      try {
        const res = await nestRequest<unknown>(
          API_ROUTES.profiles.superAdminLookup({ userId }),
        );
        return toProfileLookupRow(
          superAdminProfileLookupResponseSchema.parse(res).profile,
        );
      } catch (err) {
        reportNestFallback("profiles.superAdminLookupById", err);
      }
    }
    const { data, error } = await supabase
      .from("profiles")
      .select("id, full_name, full_name_en, phone, nafath_verified")
      .eq("id", userId)
      .maybeSingle();
    if (error || !data) return null;
    return data;
  },

  /**
   * Cross-tenant profile lookup by phone (Grant-Super-Admin dialog). Takes the
   * FE-computed storage variants (`["+9665…", "9665…"]`); the Nest path sends
   * the canonical first variant and the SERVER re-expands the same set.
   * THROWS on a Supabase error (parity with the dialog's phone lookup, which
   * surfaces `error.message` to the user — unlike the by-id lookup).
   */
  async superAdminLookupByPhone(
    variants: string[],
  ): Promise<ProfileLookupRow | null> {
    if (API_URL) {
      try {
        const res = await nestRequest<unknown>(
          API_ROUTES.profiles.superAdminLookup({ phone: variants[0] }),
        );
        return toProfileLookupRow(
          superAdminProfileLookupResponseSchema.parse(res).profile,
        );
      } catch (err) {
        reportNestFallback("profiles.superAdminLookupByPhone", err);
      }
    }
    const { data, error } = await supabase
      .from("profiles")
      .select("id, full_name, full_name_en, phone, nafath_verified")
      .in("phone", variants)
      .limit(1)
      .maybeSingle();
    if (error) throw error;
    return data ?? null;
  },
};

/** Shared full-row read (`select('*')` + maybeSingle) — throws on error. */
async function fetchProfileRow(userId: string): Promise<ProfileRow | null> {
  const { data, error } = await supabase
    .from("profiles")
    .select("*")
    .eq("id", userId)
    .maybeSingle();
  if (error) throw error;
  return data ?? null;
}
